import { Injectable, inject } from '@angular/core';
import { Order } from 'src/app/checkout/model/order.model';
import { Cart, CreateQuotation } from 'src/app/core/services/cart/cart-api.service';
import { Product } from 'src/app/products/models/product.model';
import { FacebookAction, FacebookAnalyticsService } from '../analytics/facebook-analytics.service';
import { Action, GoogleAnalyticsService } from '../analytics/google-analytics.service';

interface OrderDetails {
  amount_paid: string;
  order_number: string;
  cash_points?: number;
  order_id?: string;
}
@Injectable({
  providedIn: 'root'
})
export class ConversionTrackingService {
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private facebookAnalyticsService = inject(FacebookAnalyticsService);


  trackRegistrationConversion() {
    this.googleAnalyticsService.injectScript({
      action: Action.Register,
      value: 1,
      category: 'ecommerce',
    });

    this.facebookAnalyticsService.injectScript({
      action: FacebookAction.Register,
      value: 1,
    });
  }

  trackAddToCartConversion(cart: Cart) {
    // Ensure cart has safe cart_items array
    const safeCartItems = cart?.cart_items || [];

    this.googleAnalyticsService.injectScript({
      action: Action.AddToCart,
      value: cart?.cart_total,
      category: 'ecommerce',
      items: safeCartItems.map(item => ({  // Safe: Use fallback array
        id: item.product?.id,  // Assuming product is an ID or object
        name: item.product?.name,  // Adjust based on your Product model
        price: item.effective_price || 0,
        quantity: item.quantity || 1
      }))
    });

    // inject fbqa script add to cart
    this.facebookAnalyticsService.injectScript({
      action: FacebookAction.AddToCart,  // Note: May need to align with FacebookAction if enum mismatch
      value: cart?.cart_total,
      contents: safeCartItems.map(item => ({  // Safe: Use fallback array
        id: item.product?.id,
        name: item.product?.name,
        price: item.effective_price,
        quantity: item.quantity || 1
      })) as any[]
    });
  }

  trackProductDetailViewConversion(product: Product) {
    this.googleAnalyticsService.injectScript({
      action: Action.ViewContent,
      value: 1,
      category: 'ecommerce',
      items: [],
      content_name: product?.name,
      transaction_id: product?.id
    });

    this.facebookAnalyticsService.injectScript({
      action: FacebookAction.ViewContent,
      value: 1,
      contents: [],
      content_name: product?.name
    });
  }

  trackCheckoutInitiationConversion(createdQuotation: CreateQuotation) {
    this.googleAnalyticsService.injectScript({
      action: Action.Checkout,
      value: createdQuotation?.quotation?.quotation_total
    });

    this.facebookAnalyticsService.injectScript({
      action: FacebookAction.Checkout,
      value: createdQuotation?.quotation?.quotation_total
    });
  }

  trackPurchaseConversion(orderDetails: OrderDetails) {
    // Google Analytics Purchase tracking
    this.googleAnalyticsService.injectScript({
      action: Action.Purchase,
      value: +orderDetails?.amount_paid,
      category: 'ecommerce',
      items: [],
      content_name: '',
      transaction_id: orderDetails?.order_number  // Required for GA4 conversion matching
    });

    // Meta (Facebook Pixel) Purchase tracking
    this.facebookAnalyticsService.injectScript({
      action: FacebookAction.Purchase,
      value: +orderDetails?.amount_paid
    });
  }

  trackAddPaymentInfoConversion(order: Order) {
    this.googleAnalyticsService.injectScript({
      action: Action.AddPaymentInfo,
      value: order?.order_total
    });

    this.facebookAnalyticsService.injectScript({
      action: FacebookAction.AddPaymentInfo,
      value: order?.order_total
    });
  }


  trackSearchConversion(searchQuery: string) {
    this.googleAnalyticsService.injectScript({
      action: Action.Search,
      value: 1,
      category: 'ecommerce',
      items: [],
      content_name: searchQuery
    });

    this.facebookAnalyticsService.injectScript({
      action: FacebookAction.Search,
      value: 1,
      contents: [],
      content_name: searchQuery
    });
  }

  trackPaymentSuccessConversion(orderDetails: OrderDetails) {
    this.googleAnalyticsService.injectScript({
      action: Action.PaymentSuccess,
      value: +orderDetails?.amount_paid
    });

    this.facebookAnalyticsService.injectScript({
      action: FacebookAction.PaymentSuccess,
      value: +orderDetails?.amount_paid,
      contents: [],
      content_name: '',
      custom_event: true
    });

  }

  trackPaymentFailureConversion() {
    this.googleAnalyticsService.injectScript({
      action: Action.PaymentFailure,
      value: 1
    });

    this.facebookAnalyticsService.injectScript({
      action: FacebookAction.PaymentFailure,
      value: 1,
      custom_event: true
    });
  }

  trackLoginConversion() {
    this.googleAnalyticsService.injectScript({
      action: Action.Login,
      value: 1
    });

    this.facebookAnalyticsService.injectScript({
      action: FacebookAction.Login,
      value: 1,
      custom_event: true
    });
  }

  removeConversionTracking(action: keyof typeof Action | keyof typeof FacebookAction) {
    this.googleAnalyticsService.removeScript(Action[action]);
    this.facebookAnalyticsService.removeScript(FacebookAction[action]);
  }
}
